import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  ISO_639_1_LANGUAGE_CODES,
  mapEmailTemplatesWrapResponseToDto,
  mapJurisdictionEmailTemplatesResponseToDto,
} from '../dto/system-email-templates.dto.js';
import { mapJurisdictionsResponseToDto } from '../../system-kyc/dto/system-kyc.dto.js';

const DEFAULT_LANGUAGE = 'en';

function normalizeLanguage(value) {
  return String(value || '').trim().toLowerCase() || DEFAULT_LANGUAGE;
}

export function useSystemEmailTemplatesWrap() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryJurisdictionId = String(searchParams.get('jurisdictionId') || '').trim();
  const queryLanguage = normalizeLanguage(searchParams.get('lang'));

  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState(queryJurisdictionId);
  const [selectedLanguage, setSelectedLanguage] = useState(queryLanguage);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [templateLanguages, setTemplateLanguages] = useState([]);

  const languageOptions = useMemo(
    () => Array.from(new Set([...ISO_639_1_LANGUAGE_CODES, ...templateLanguages.map(normalizeLanguage)])),
    [templateLanguages],
  );

  const listLink = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedJurisdictionId) {
      params.set('jurisdictionId', selectedJurisdictionId);
    }
    if (selectedLanguage) {
      params.set('lang', selectedLanguage);
    }

    const query = params.toString();
    return `/system/user-settings/email-templates/list${query ? `?${query}` : ''}`;
  }, [selectedJurisdictionId, selectedLanguage]);

  useEffect(() => {
    setSelectedJurisdictionId(queryJurisdictionId);
  }, [queryJurisdictionId]);

  useEffect(() => {
    setSelectedLanguage(queryLanguage);
  }, [queryLanguage]);

  const updateQuery = useCallback((nextJurisdictionId, nextLanguage) => {
    const nextParams = new URLSearchParams(searchParams);
    const jurisdictionId = String(nextJurisdictionId || '').trim();
    const language = normalizeLanguage(nextLanguage);

    if (jurisdictionId) {
      nextParams.set('jurisdictionId', jurisdictionId);
    } else {
      nextParams.delete('jurisdictionId');
    }

    if (language) {
      nextParams.set('lang', language);
    } else {
      nextParams.delete('lang');
    }

    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadJurisdictions = useCallback(async () => {
    setIsJurisdictionsLoading(true);

    try {
      const response = await adminApi.getJurisdictions(token);
      const list = mapJurisdictionsResponseToDto(response);
      setJurisdictions(list);

      if (list.length === 0) {
        setSelectedJurisdictionId('');
        updateQuery('', selectedLanguage);
        return;
      }

      const hasSelected = list.some((item) => item.id === queryJurisdictionId);
      if (hasSelected) {
        return;
      }

      const fallbackId = list[0].id;
      setSelectedJurisdictionId(fallbackId);
      updateQuery(fallbackId, selectedLanguage);
    } catch (requestError) {
      if (requestError?.status === 404) {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        updateQuery('', selectedLanguage);
      } else {
        setJurisdictions([]);
        setSelectedJurisdictionId('');
        setError(requestError.message || 'Failed to load jurisdictions');
      }
    } finally {
      setIsJurisdictionsLoading(false);
    }
  }, [queryJurisdictionId, selectedLanguage, token, updateQuery]);

  const loadWrap = useCallback(async () => {
    if (isJurisdictionsLoading) {
      return;
    }

    const isKnownJurisdiction = jurisdictions.some((item) => item.id === selectedJurisdictionId);
    if (!selectedJurisdictionId || !isKnownJurisdiction) {
      setHeader('');
      setFooter('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const listResponse = await adminApi.requestAdmin({
        token,
        method: 'GET',
        path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates`,
      });
      const templates = mapJurisdictionEmailTemplatesResponseToDto(listResponse);
      const languages = Array.from(new Set(templates.map((item) => normalizeLanguage(item.language))));
      setTemplateLanguages(languages);

      const response = await adminApi.requestAdmin({
        token,
        method: 'GET',
        path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates/wrap/${encodeURIComponent(selectedLanguage)}`,
      });

      const dto = mapEmailTemplatesWrapResponseToDto(response);
      setHeader(dto.header);
      setFooter(dto.footer);
    } catch (requestError) {
      if (requestError?.status === 404) {
        setHeader('');
        setFooter('');
      } else {
        setError(requestError.message || 'Failed to load header and footer');
        setHeader('');
        setFooter('');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isJurisdictionsLoading, jurisdictions, selectedJurisdictionId, selectedLanguage, token]);

  useEffect(() => {
    loadJurisdictions();
  }, [loadJurisdictions]);

  useEffect(() => {
    loadWrap();
  }, [loadWrap]);

  const changeJurisdiction = (jurisdictionId) => {
    const nextId = String(jurisdictionId || '').trim();
    setSelectedJurisdictionId(nextId);
    updateQuery(nextId, selectedLanguage);
  };

  const changeLanguage = (language) => {
    const nextLanguage = normalizeLanguage(language);
    setSelectedLanguage(nextLanguage);
    updateQuery(selectedJurisdictionId, nextLanguage);
  };

  const saveWrap = async () => {
    if (!selectedJurisdictionId) {
      setError('Jurisdiction is required');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.requestAdmin({
        token,
        method: 'PUT',
        path: `/admin/system/user-settings/jurisdictions/${encodeURIComponent(selectedJurisdictionId)}/email-templates/wrap/${encodeURIComponent(selectedLanguage)}`,
        body: {
          header,
          footer,
        },
      });

      const dto = mapEmailTemplatesWrapResponseToDto(response);
      setHeader(dto.header || header);
      setFooter(dto.footer || footer);
      setSuccess('Header and footer updated.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to save header and footer');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    header,
    footer,
    jurisdictions,
    selectedJurisdictionId,
    selectedLanguage,
    languageOptions,
    isJurisdictionsLoading,
    isLoading,
    isSaving,
    error,
    success,
    listLink,
    setHeader,
    setFooter,
    changeJurisdiction,
    changeLanguage,
    saveWrap,
  };
}
